import {
  Component,
  ElementRef,
  ViewChild,
  computed,
  effect,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CaseChatService } from './case-chat.service';
import { MarkdownPipe } from '../../../shared/pipes/markdown.pipe';

const CHIPS = [
  'Key issues for hearing',
  'Summarise the documents',
  "Defendant's likely defences",
  'Draft directions',
  'Relevant case law',
];

@Component({
  selector: 'app-case-chat-drawer',
  standalone: true,
  imports: [CommonModule, FormsModule, MatProgressSpinnerModule, MarkdownPipe],
  template: `
    <!-- Backdrop overlay -->
    <div class="chat-overlay" *ngIf="isOpen()" (click)="service.close()"></div>

    <!-- Drawer -->
    <div class="chat-drawer" [class.open]="isOpen()">
      <!-- Header -->
      <div class="chat-drawer-header">
        <div
          class="cm-av cm-av-ai"
          style="width:26px;height:26px;font-size:9px;flex-shrink:0"
        >
          AI
        </div>
        <div style="flex:1;min-width:0">
          <div
            style="font-size:12px;font-weight:600;color:var(--ink);white-space:nowrap;overflow:hidden;text-overflow:ellipsis"
          >
            Chat with Case ·
            <span class="mono-sm text-brand">{{
              service.activeCase()?.reference
            }}</span>
          </div>
          <div
            style="font-size:10px;color:var(--steel-3);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;text-transform:capitalize"
          >
            {{ service.activeCase()?.parties }}
          </div>
        </div>
        <button
          class="chat-drawer-close"
          (click)="service.close()"
          title="Close"
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      <!-- Indexing spinner -->
      <div *ngIf="service.indexing()" class="chat-indexing">
        <mat-spinner [diameter]="20"></mat-spinner>
        <span>Indexing case documents…</span>
      </div>

      <!-- Message thread -->
      <div
        class="chat-scroll"
        #scrollEl
        *ngIf="!service.indexing()"
        (scroll)="onScroll($event)"
      >
        <div class="chat-thread">
          <!-- Privacy notice -->
          <div class="chat-system">
            <div class="chat-system-text">
              <svg
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--steel-3)"
                stroke-width="1.5"
                style="vertical-align:-1px;margin-right:3px"
              >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              Private · Advisory only · Not discoverable by parties · NDPA 2023
            </div>
          </div>

          <!-- Messages -->
          <div *ngFor="let msg of service.messages()" class="cm">
            <div class="cm-head">
              <div
                class="cm-av"
                [class.cm-av-ai]="msg.role === 'ai'"
                [class.cm-av-user]="msg.role === 'user'"
              >
                {{ msg.role === 'ai' ? 'AI' : 'JG' }}
              </div>
              <span class="cm-name">{{
                msg.role === 'ai' ? 'JusticeFlow AI' : 'Your Honour'
              }}</span>
              <span class="cm-time">{{ msg.time }}</span>
            </div>
            <div class="cm-body">
              <!-- Typing indicator dots (no text yet) -->
              <div *ngIf="msg.typing && !msg.text" class="chat-typing">
                <span></span><span></span><span></span>
              </div>
              <!-- Markdown-rendered text -->
              <div
                *ngIf="msg.text"
                class="cm-md"
                [innerHTML]="msg.text | markdown"
              ></div>
              <!-- Streaming cursor appended after content -->
              <span *ngIf="msg.typing && msg.text" class="chat-cursor">▋</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Bottom: chips + input -->
      <div class="chat-bottom" *ngIf="!service.indexing()">
        <div class="chat-suggest">
          <div
            *ngFor="let chip of chips"
            class="chat-chip"
            (click)="sendChip(chip)"
          >
            {{ chip }}
          </div>
        </div>
        <div class="chat-input-area">
          <div class="chat-input-box">
            <textarea
              #inputEl
              class="chat-input"
              [(ngModel)]="draft"
              placeholder="Ask about this case…"
              rows="1"
              (keydown)="onKey($event)"
              (input)="autoResize()"
            ></textarea>
            <button
              class="chat-send"
              (click)="send()"
              [disabled]="!draft.trim() || service.streaming()"
              title="Send"
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                stroke-width="2.5"
              >
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .chat-overlay {
        position: fixed;
        inset: 0;
        background: rgba(15, 23, 42, 0.45);
        z-index: 199;
        backdrop-filter: blur(1px);
        animation: fadeIn 0.2s ease;
      }
      @keyframes fadeIn {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }

      .chat-drawer {
        position: fixed;
        top: 0;
        right: 0;
        width: 400px;
        height: 100vh;
        background: var(--white);
        border-left: 1px solid var(--ash-2);
        display: flex;
        flex-direction: column;
        z-index: 200;
        transform: translateX(100%);
        transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
      }
      .chat-drawer.open {
        transform: translateX(0);
      }

      .chat-drawer-header {
        padding: 0 14px;
        height: 52px;
        border-bottom: 1px solid var(--ash-2);
        display: flex;
        align-items: center;
        gap: 10px;
        flex-shrink: 0;
      }
      .chat-drawer-close {
        width: 26px;
        height: 26px;
        border-radius: var(--r);
        border: 1px solid var(--ash-2);
        background: var(--white);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        color: var(--steel-2);
        flex-shrink: 0;
      }
      .chat-drawer-close:hover {
        background: var(--ash-3);
      }

      .chat-indexing {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 12px;
        font-size: 12px;
        color: var(--steel-2);
      }

      .chat-scroll {
        flex: 1;
        overflow-y: auto;
        background: var(--bone);
      }
      .chat-thread {
        padding: 12px 14px;
      }

      .chat-system {
        text-align: center;
        padding: 10px 8px 6px;
      }
      .chat-system-text {
        font-size: 10px;
        color: var(--steel-3);
        line-height: 1.6;
      }

      .cm {
        padding: 12px 0;
      }
      .cm + .cm {
        border-top: 1px solid var(--ash-2);
      }
      .cm-head {
        display: flex;
        align-items: center;
        gap: 7px;
        margin-bottom: 5px;
      }
      .cm-av {
        width: 20px;
        height: 20px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 7px;
        font-weight: 700;
        flex-shrink: 0;
      }
      .cm-av-ai {
        background: var(--brand-dim);
        color: var(--brand);
      }
      .cm-av-user {
        background: var(--ink);
        color: #fff;
      }
      .cm-name {
        font-size: 11px;
        font-weight: 600;
        color: var(--ink);
      }
      .cm-time {
        font-size: 9px;
        color: var(--steel-3);
        margin-left: auto;
      }
      .cm-body {
        font-size: 12px;
        line-height: 1.7;
        color: var(--ink-2);
        padding-left: 27px;
      }

      /* Markdown output styles */
      .cm-md p {
        margin: 0 0 6px;
      }
      .cm-md p:last-child {
        margin-bottom: 0;
      }
      .cm-md strong {
        font-weight: 700;
        color: var(--ink);
      }
      .cm-md em {
        font-style: italic;
      }
      .cm-md ul,
      .cm-md ol {
        margin: 4px 0 6px;
        padding-left: 18px;
      }
      .cm-md li {
        margin-bottom: 3px;
      }
      .cm-md code {
        font-family: var(--mono);
        font-size: 11px;
        background: var(--ash-3);
        border: 1px solid var(--ash-2);
        border-radius: 3px;
        padding: 1px 4px;
      }
      .cm-md pre {
        background: var(--ash-3);
        border: 1px solid var(--ash-2);
        border-radius: var(--r);
        padding: 8px 10px;
        overflow-x: auto;
        margin: 6px 0;
      }
      .cm-md pre code {
        background: none;
        border: none;
        padding: 0;
        font-size: 11px;
      }
      .cm-md blockquote {
        border-left: 3px solid var(--brand);
        margin: 6px 0;
        padding: 4px 10px;
        color: var(--steel);
        background: var(--brand-dim);
        border-radius: 0 var(--r) var(--r) 0;
      }
      .cm-md h1,
      .cm-md h2,
      .cm-md h3 {
        font-size: 12px;
        font-weight: 700;
        color: var(--ink);
        margin: 8px 0 4px;
      }
      .cm-md hr {
        border: none;
        border-top: 1px solid var(--ash-2);
        margin: 8px 0;
      }

      .chat-cursor {
        display: inline-block;
        animation: blink-cursor 0.6s step-end infinite;
        color: var(--brand);
        font-size: 11px;
        margin-left: 1px;
      }
      @keyframes blink-cursor {
        0%,
        100% {
          opacity: 1;
        }
        50% {
          opacity: 0;
        }
      }

      .chat-typing {
        display: inline-flex;
        gap: 3px;
        padding: 4px 0;
      }
      .chat-typing span {
        width: 4px;
        height: 4px;
        background: var(--steel-3);
        border-radius: 50%;
        animation: blink 1.4s infinite;
      }
      .chat-typing span:nth-child(2) {
        animation-delay: 0.2s;
      }
      .chat-typing span:nth-child(3) {
        animation-delay: 0.4s;
      }
      @keyframes blink {
        0%,
        80%,
        100% {
          opacity: 0.3;
        }
        40% {
          opacity: 1;
        }
      }

      .chat-bottom {
        flex-shrink: 0;
        background: var(--white);
        border-top: 1px solid var(--ash-2);
      }
      .chat-suggest {
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
        padding: 8px 12px 4px;
      }
      .chat-chip {
        padding: 4px 10px;
        border: 1px solid var(--ash-2);
        border-radius: 20px;
        font-size: 10.5px;
        color: var(--steel);
        background: var(--white);
        cursor: pointer;
        transition: all 0.15s;
        white-space: nowrap;
        user-select: none;
      }
      .chat-chip:hover {
        border-color: var(--brand);
        color: var(--brand);
        background: var(--brand-dim);
      }

      .chat-input-area {
        padding: 6px 12px 12px;
      }
      .chat-input-box {
        display: flex;
        align-items: flex-end;
        gap: 6px;
        background: var(--bone);
        border: 1px solid var(--ash-2);
        border-radius: 18px;
        padding: 4px 4px 4px 12px;
        transition:
          border-color 0.15s,
          box-shadow 0.15s;
      }
      .chat-input-box:focus-within {
        border-color: var(--brand);
        box-shadow: 0 0 0 2px var(--brand-dim);
        background: var(--white);
      }
      .chat-input {
        flex: 1;
        border: none;
        outline: none;
        font-size: 12px;
        resize: none;
        min-height: 20px;
        max-height: 80px;
        line-height: 1.5;
        font-family: var(--sans);
        color: var(--ink-2);
        background: transparent;
        padding: 2px 0;
      }
      .chat-input::placeholder {
        color: var(--steel-3);
      }
      .chat-send {
        width: 26px;
        height: 26px;
        border-radius: 50%;
        background: var(--brand);
        border: none;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        transition: background 0.15s;
      }
      .chat-send:hover {
        background: var(--brand-deep);
      }
      .chat-send:disabled {
        opacity: 0.4;
        cursor: not-allowed;
      }
    `,
  ],
})
export class CaseChatDrawerComponent {
  @ViewChild('scrollEl') scrollEl?: ElementRef<HTMLElement>;
  @ViewChild('inputEl') inputEl?: ElementRef<HTMLTextAreaElement>;

  readonly chips = CHIPS;
  draft = '';

  readonly isOpen = computed(() => this.service.activeCase() !== null);

  private userScrolledUp = false;

  constructor(readonly service: CaseChatService) {
    let lastCaseId: string | null = null;

    effect(() => {
      this.service.messages();
      this.service.streaming();

      const caseId = this.service.activeCase()?.caseId ?? null;
      if (caseId !== lastCaseId) {
        lastCaseId = caseId;
        this.userScrolledUp = false;
      }

      if (!this.userScrolledUp) {
        setTimeout(() => this.scrollToBottom());
      }
    });
  }

  send() {
    const text = this.draft.trim();
    if (!text) return;
    this.draft = '';
    if (this.inputEl) {
      this.inputEl.nativeElement.style.height = 'auto';
    }
    // Sending a message always scrolls back to bottom
    this.userScrolledUp = false;
    this.service.send(text);
  }

  sendChip(text: string) {
    this.userScrolledUp = false;
    this.service.send(text);
  }

  onKey(e: KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.send();
    }
  }

  autoResize() {
    const el = this.inputEl?.nativeElement;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
  }

  onScroll(e: Event) {
    const el = e.target as HTMLElement;
    this.userScrolledUp = el.scrollTop + el.clientHeight < el.scrollHeight - 60;
  }

  private scrollToBottom() {
    const el = this.scrollEl?.nativeElement;
    if (el) el.scrollTop = el.scrollHeight;
  }
}
